<template>
  <div class="question-bank-page">
    <div class="question-bank-container">
      <div class="page-header">
        <h1>题库中心</h1>
      </div>

      <!-- 过滤器区域 -->
      <div class="filter-section">
        <div class="filter-group">
          <h3>学段选择</h3>
          <div class="tags">
            <span 
              v-for="level in levels" 
              :key="level.value"
              class="tag"
              :class="{ active: selectedLevel === level.value }"
              @click="selectedLevel = level.value"
            >
              {{ level.label }}
            </span>
          </div>
        </div>

        <div class="filter-group">
          <h3>题型分类</h3>
          <div class="tags">
            <span 
              v-for="type in questionTypes" 
              :key="type.value"
              class="tag"
              :class="{ active: selectedType === type.value }"
              @click="selectedType = type.value"
            >
              {{ type.label }}
            </span>
          </div>
        </div>
      </div>

      <!-- 题目列表 -->
      <div class="questions-list">
        <div v-if="loading" class="loading-state">
          <el-icon class="is-loading"><Loading /></el-icon>
          <span>加载题库中...</span>
        </div>
        <div v-else-if="paginatedQuestions.length === 0" class="empty-state">
          <el-icon><Document /></el-icon>
          <span>暂无题目数据</span>
        </div>
        <div 
          v-else
          v-for="(question, index) in paginatedQuestions" 
          :key="question.id || index"
          class="question-card"
        >
          <div class="question-header">
            <div class="question-info">
              <span class="badge badge-primary">{{ question.type }}</span>
              <span class="badge badge-success">{{ question.level }}</span>
            </div>
          </div>
          
          <div class="question-content">
            <p class="question-text">{{ question.title }}</p>
            
            <!-- 题目图片 -->
            <div v-if="question.titleImages && question.titleImages.length > 0" class="question-images">
              <img 
                v-for="(image, imgIndex) in question.titleImages" 
                :key="image.id || imgIndex"
                :src="image.imageUrl"
                :alt="`题目图片 ${imgIndex + 1}`"
                class="question-image"
                @click="openImagePreview(image.imageUrl)"
                style="cursor: pointer;"
              />
            </div>
            
            <!-- 选择题选项 -->
            <div class="question-options">
              <div v-if="question.options && question.options.length > 0">
                <div 
                  v-for="(option, optIndex) in question.options" 
                  :key="option.id || optIndex"
                  class="option"
                  :class="{ correct: question.showAnswer && option.isCorrect }"
                >
                  {{ option.key }}. {{ option.text }}
                  <!-- 选项图片 -->
                  <div v-if="option.optionImages && option.optionImages.length > 0" class="option-images">
                    <img 
                      v-for="(img, imgIndex) in option.optionImages" 
                      :key="img.id || imgIndex"
                      :src="img.imageUrl"
                      :alt="`选项图片 ${imgIndex + 1}`"
                      class="option-image"
                      @click="openImagePreview(img.imageUrl)"
                      style="cursor: pointer;"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="no-options">
                暂无选项
              </div>
            </div>
          </div>

          <!-- 答案区域 -->
          <div class="answer-section">
            <div class="answer-toggle">
              <button class="toggle-btn" @click="toggleAnswer(question)">
                {{ question.showAnswer ? '收起答案' : '查看答案' }}
              </button>
            </div>
            <div class="answer-content" v-show="question.showAnswer">
              <div class="answer-header">
                <span class="answer-label">正确答案：</span>
                <span class="answer-value">{{ question.answer }}</span>
              </div>
              <div class="answer-explanation" v-if="question.explanation">
                <h4>解析：</h4>
                <p>{{ question.explanation }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <button 
          class="page-btn" 
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          上一页
        </button>
        
        <!-- 第一页 -->
        <button 
          v-if="totalPages > 0"
          class="page-btn"
          :class="{ active: currentPage === 1 }"
          @click="currentPage = 1"
        >
          1
        </button>
        
        <!-- 前面的省略号 -->
        <span v-if="currentPage > 4" class="page-ellipsis">...</span>
        
        <!-- 中间的页码 -->
        <button 
          v-for="page in visiblePages" 
          :key="page"
          class="page-btn"
          :class="{ active: currentPage === page }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        
        <!-- 后面的省略号 -->
        <span v-if="currentPage < totalPages - 3" class="page-ellipsis">...</span>
        
        <!-- 最后一页 -->
        <button 
          v-if="totalPages > 1"
          class="page-btn"
          :class="{ active: currentPage === totalPages }"
          @click="currentPage = totalPages"
        >
          {{ totalPages }}
        </button>
        
        <button 
          class="page-btn" 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          下一页
        </button>
      </div>
    </div>
    
    <!-- 图片预览模态框 -->
    <div v-if="showImagePreview" class="image-preview-modal" @click="closeImagePreview">
      <div class="image-preview-content" @click.stop>
        <button class="close-btn" @click="closeImagePreview">&times;</button>
        <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
    </div>
  </div>
</template>

<script>
import { getQuestionbankList } from '@/api/droneApi';
import { Loading, Document } from '@element-plus/icons-vue';
import { baseURL } from '@/api/axiosConfig';

export default {
  name: 'QuestionBank',
  components: {
    Loading,
    Document
  },
  data() {
    return {
      selectedLevel: 'all',
      selectedType: 'all',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
      questions: [],
      loading: false,
      showImagePreview: false,
      previewImageUrl: '',
      levels: [
        { value: 'all', label: '全部' },
        { value: '1', label: '小学' },
        { value: '2', label: '初中' },
        { value: '3', label: '高中' }
      ],
      questionTypes: [
        { value: 'all', label: '全部' },
        { value: '0', label: '单选题' },
        { value: '1', label: '多选题' }
      ]
    }
  },
  computed: {
    paginatedQuestions() {
      return this.questions
    },
    visiblePages() {
      const pages = []
      const start = Math.max(2, this.currentPage - 1)
      const end = Math.min(this.totalPages - 1, this.currentPage + 1)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    }
  },
  watch: {
    selectedLevel() {
      this.currentPage = 1
      this.fetchQuestions()
    },
    selectedType() {
      this.currentPage = 1
      this.fetchQuestions()
    },
    currentPage() {
      this.fetchQuestions()
    },
    pageSize() {
      this.currentPage = 1
      this.fetchQuestions()
    }
  },
  mounted() {
    this.fetchQuestions()
  },
  methods: {
    async fetchQuestions() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }
        
        if (this.selectedLevel !== 'all') {
          params.difficulty = parseInt(this.selectedLevel)
        }
        
        if (this.selectedType !== 'all') {
          params.questionType = this.selectedType
        }
        
        const res = await getQuestionbankList(params)
        
        console.log('接口返回数据:', res)
        console.log('data字段:', res.data)
        console.log('内层data字段:', res.data.data)
        
        if (res.data.code === 200 && res.data.data) {
          // 确保list是数组
          const listData = Array.isArray(res.data.data.list) ? res.data.data.list : []
          console.log('处理前的list数据:', listData)
          console.log('list长度:', listData.length)
          
          this.questions = listData.map((item, index) => {
            console.log(`处理第${index}个item:`, item)
            // 处理空对象的情况
            if (!item || Object.keys(item).length === 0) {
              console.log(`第${index}个item是空对象`)
              return {
                id: Math.random(),
                type: '单选题',
                level: '小学',
                typeValue: '0',
                levelValue: '1',
                title: '暂无题目内容',
                options: [],
                answer: '',
                explanation: '暂无解析',
                favorited: false,
                showAnswer: true
              }
            }
            
            // 处理真实数据
            const transformedItem = {
              ...item,
              type: item.questionType === '0' ? '单选题' : '多选题',
              level: item.difficulty === 1 ? '小学' : item.difficulty === 2 ? '初中' : '高中',
              typeValue: item.questionType,
              levelValue: item.difficulty ? item.difficulty.toString() : '1',
              title: item.questionTitle || '暂无题目内容',
              titleImages: item.titleImages ? item.titleImages.map(img => ({
                ...img,
                imageUrl: baseURL + img.imageUrl
              })) : [],
              options: item.options ? item.options.map(opt => ({
                key: opt.optionLabel || '',
                text: opt.optionContent || '',
                isCorrect: opt.isCorrect === 1,
                optionImages: opt.optionImages ? opt.optionImages.map(img => ({
                  ...img,
                  imageUrl: baseURL + img.imageUrl
                })) : []
              })) : [],
              answer: this.getAnswerText(item),
              explanation: item.analysis || '暂无解析',
              favorited: false,
              showAnswer: true
            }
            
            console.log(`转换后第${index}个item:`, transformedItem)
            return transformedItem
          })
          
          console.log('转换后的questions:', this.questions)
          console.log('转换后的questions长度:', this.questions.length)
          
          this.total = res.data.data.total
          this.totalPages = res.data.data.pages
        }
      } catch (error) {
        console.error('获取题库失败:', error)
      } finally {
        this.loading = false
        // 滚动到页面顶部
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    getAnswerText(question) {
      if (!question || !question.options || !Array.isArray(question.options)) {
        return '暂无答案'
      }
      const correctOptions = question.options
        .filter(opt => opt && (opt.isCorrect === 1 || opt.iscorrect === 1))
        .map(opt => opt.optionLabel || opt.optionlabel)
        .join('')
      return correctOptions || '暂无答案'
    },
    toggleFavorite(question) {
      question.favorited = !question.favorited
    },
    toggleAnswer(question) {
      question.showAnswer = !question.showAnswer
    },
    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl
      this.showImagePreview = true
    },
    closeImagePreview() {
      this.showImagePreview = false
      this.previewImageUrl = ''
    }
  }
}
</script>

<style scoped>
.question-bank-page {
  padding: 20px;
  min-height: calc(100vh - 200px);
  background: #f5f7fa;
}

.question-bank-container {
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 25px;
}

.page-header h1 {
  font-size: 28px;
  color: #1a5f7a;
  font-weight: bold;
}

/* 过滤器区域 */
.filter-section {
  background: white;
  border-radius: 12px;
  padding: 20px 25px;
  margin-bottom: 25px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.filter-group {
  margin-bottom: 20px;
}

.filter-group:last-child {
  margin-bottom: 0;
}

.filter-group h3 {
  font-size: 14px;
  color: #666;
  margin: 0 0 12px;
  font-weight: 500;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag {
  padding: 6px 16px;
  background: #f0f2f5;
  color: #666;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.tag:hover {
  background: #e8f4f8;
  color: #1a5f7a;
}

.tag.active {
  background: #1a5f7a;
  color: white;
}

/* 题目卡片 */
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;
}

.question-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
}

.question-info {
  display: flex;
  gap: 10px;
}

.badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-primary {
  background: #e8f4f8;
  color: #1a5f7a;
}

.badge-success {
  background: #e8f9ed;
  color: #27ae60;
}

.question-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f2f5;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  color: #999;
}

.btn-icon:hover {
  background: #ffeaea;
  color: #e74c3c;
}

.btn-icon.active {
  background: #ffeaea;
  color: #e74c3c;
}

.question-content {
  padding: 20px;
}

.question-text {
  font-size: 16px;
  color: #333;
  margin: 0 0 15px;
  line-height: 1.6;
}

.question-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option {
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 14px;
  color: #555;
}

.option.correct {
  background: #e8f9ed;
  color: #27ae60;
  font-weight: 500;
}

.no-options {
  padding: 20px;
  text-align: center;
  color: #999;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 14px;
}

/* 题目图片 */
.question-images {
  margin: 15px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.question-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* 选项图片 */
.option-images {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.option-image {
  max-width: 100px;
  max-height: 100px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.question-input {
  margin-top: 10px;
}

.question-input .form-control {
  width: 200px;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #f8f9fa;
  color: #333;
}

/* 答案区域 */
.answer-section {
  padding: 15px 20px;
  background: #fffbf0;
  border-top: 1px solid #fff3cd;
}

.answer-toggle {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.toggle-btn {
  padding: 6px 16px;
  background: #1a5f7a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  background: #134a5e;
}

.answer-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.answer-label {
  font-size: 14px;
  color: #856404;
  font-weight: 500;
}

.answer-value {
  font-size: 14px;
  color: #27ae60;
  font-weight: bold;
}

.answer-explanation h4 {
  font-size: 14px;
  color: #856404;
  margin: 0 0 8px;
  font-weight: 500;
}

.answer-explanation p {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.7;
}

/* 加载和空状态 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #666;
  font-size: 16px;
}

.loading-state .el-icon,
.empty-state .el-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: #1a5f7a;
}

.loading-state .is-loading {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
  padding: 20px 0;
}

.page-btn {
  padding: 8px 14px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #1a5f7a;
  border-color: #1a5f7a;
  color: white;
}

.page-btn.active {
  background: #1a5f7a;
  border-color: #1a5f7a;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-ellipsis {
  padding: 8px 14px;
  font-size: 14px;
  color: #666;
  cursor: default;
}

/* 图片预览模态框 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.image-preview-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 50%;
  font-size: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f0f0f0;
}

.preview-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}
/* 响应式设计 */
@media (max-width: 768px) {
  .question-bank-page {
    padding: 10px;
    min-height: calc(100vh - 180px);
  }
  
  .question-bank-container {
    max-width: 100%;
  }
  
  .page-header h1 {
    font-size: 22px;
  }
  
  .filter-section {
    padding: 15px;
  }
  
  .tags {
    gap: 8px;
  }
  
  .tag {
    padding: 5px 12px;
    font-size: 13px;
  }
  
  .question-card {
    border-radius: 8px;
  }
  
  .question-header {
    padding: 12px 15px;
  }
  
  .question-content {
    padding: 15px;
  }
  
  .question-text {
    font-size: 14px;
  }
  
  .option {
    padding: 8px 12px;
    font-size: 13px;
  }
  
  .answer-section {
    padding: 12px 15px;
  }
  
  .toggle-btn {
    padding: 5px 12px;
    font-size: 12px;
  }
  
  .pagination {
    gap: 6px;
    margin-top: 20px;
    padding: 15px 0;
  }
  
  .page-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .page-ellipsis {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .question-image {
    max-height: 150px;
  }
  
  .option-image {
    max-width: 80px;
    max-height: 80px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .question-bank-page {
    padding: 15px;
  }
  
  .question-bank-container {
    max-width: 900px;
  }
  
  .page-header h1 {
    font-size: 24px;
  }
  
  .filter-section {
    padding: 18px 20px;
  }
  
  .question-content {
    padding: 18px;
  }
  
  .question-text {
    font-size: 15px;
  }
}

@media (min-width: 1025px) and (max-width: 1440px) {
  .question-bank-container {
    max-width: 950px;
  }
}

@media (min-width: 1441px) {
  .question-bank-container {
    max-width: 1000px;
  }
}
</style>
